import { Chip } from "@heroui/react";

import { useLocale, useTranslations } from "@/src/i18n/i18n";
import {
  formatScore,
  topCategory,
  verdictOf,
} from "@/src/pages/Posts/moderationScores";
import type { ModerationScores } from "@/src/pages/Posts/posts.type";

type ScoreBadgeProps = {
  scores: ModerationScores | null;
};

/**
 * Ce que le classifieur a trouvé, en une pastille.
 *
 * ## Pourquoi cet affichage existe
 *
 * Le seuil de 0,5 est une **valeur de départ**, pas une valeur savante. Sans
 * les scores réels sous les yeux, il restera 0,5 pour toujours, faute de savoir
 * ce qu'il laisse passer. C'est la seule raison pour laquelle
 * `moderationScores` sort du serveur — l'application mobile ne le voit jamais.
 *
 * ## Trois verdicts, pas deux
 *
 * ⚠️ **« Non jugé » n'est pas « rien à signaler ».** Classifieur injoignable,
 * réponse illisible, délai dépassé : la publication attend parce qu'on n'a
 * **pas pu** la regarder. Les confondre ferait refuser par réflexe une image
 * parfaitement valide — et c'est un cas fréquent, puisque l'absence de clé
 * d'API y met *toutes* les publications.
 */
export function ScoreBadge({ scores }: ScoreBadgeProps) {
  const t = useTranslations("posts");
  const { locale } = useLocale();

  const verdict = verdictOf(scores);

  if (verdict === "unjudged") {
    return (
      <Chip color="warning" variant="soft" size="sm">
        {t("score-unjudged")}
      </Chip>
    );
  }

  const meilleure = topCategory(scores);
  const score = scores?.highestScore ?? 0;

  if (verdict === "clean") {
    return (
      <Chip color="success" variant="soft" size="sm">
        {t("score-clean", { score: formatScore(score, locale) })}
      </Chip>
    );
  }

  return (
    <Chip color="danger" variant="soft" size="sm">
      {meilleure
        ? t("score-flagged-category", {
            category: meilleure.category,
            score: formatScore(meilleure.score, locale),
          })
        : t("score-flagged", { score: formatScore(score, locale) })}
    </Chip>
  );
}
